import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { loginUser } from '@/features/auth/authSlice';
import { setError } from '@/features/error/errorSlice'; // Adjusted path to be absolute from src
import { authService } from '@/services/authService';
import { subscriptionService, SubscriptionType, RedeemCouponPayload } from '@/services/subscription.service';

interface SubscriptionState {
  type: SubscriptionType | null;
  subscriptionSource?: string | null; // 'PAID' | 'COUPON' | 'TRIAL'
  expiresAt?: string | null;
  startedAt?: string | null;
  isSubscriptionActive?: boolean;
  isCancelled?: boolean;
  subscriptionEnd?: string | null;
  remainingDays?: number;
  clientLimit?: number;
  clientCount?: number;
  canAddClients?: boolean;
  daysRemaining?: number;
  loading: boolean;
}

const initialState: SubscriptionState = {
  type: null,
  subscriptionSource: null,
  expiresAt: null,
  startedAt: null,
  isSubscriptionActive: false,
  loading: false,
};

export interface ApiError {
  response?: {
    data?: {
      message?: string;
      error?: string;
      errors?: string[];
    };
  };
  message: string;
}

export const enableTrial = createAsyncThunk('subscription/enableTrial', async (_, { dispatch }) => {
  try {
    // Step 1: Enable the trial
    const response = await subscriptionService.enableTrial();

    if (response.status === 'success') {
      try {
        // Step 2: Refresh the auth token with the latest subscription info
        // This makes a call to backend API specifically to get a new token with updated data
        const session = await authService.refreshToken();
        // Update the auth state with the new token
        dispatch(loginUser.fulfilled(session, '', { username: '', password: '' }));
      } catch (refreshError) {
        console.error('Failed to refresh token after enabling trial:', refreshError);
        // We don't want to fail the overall operation if just the token refresh fails
      }
    }

    return response;
  } catch (error) {
    const apiError = error as ApiError;
    dispatch(
      setError(
        apiError.response?.data?.error ||
          apiError.response?.data?.errors?.[0] ||
          apiError.message ||
          'Failed to enable trial',
      ),
    );
    throw error;
  }
});

export const unsubscribeAction = createAsyncThunk(
  'subscription/unsubscribe',
  async (_, { dispatch }) => {
    try {
      const response = await subscriptionService.unsubscribe();

      if (response.status === 'success') {
        try {
          // Refresh the auth token to get updated subscription info
          const session = await authService.refreshToken();
          // Update the auth state with the new token
          dispatch(loginUser.fulfilled(session, '', { username: '', password: '' }));
        } catch (refreshError) {
          console.error('Failed to refresh token after unsubscribing:', refreshError);
          // We don't want to fail the overall operation if just the token refresh fails
        }
      }

      return response;
    } catch (error) {
      const apiError = error as ApiError;
      dispatch(
        setError(
          apiError.response?.data?.error ||
            apiError.message ||
            'Failed to unsubscribe from subscription',
        ),
      );
      throw error;
    }
  },
);

export const renewSubscriptionAction = createAsyncThunk(
  'subscription/renewSubscription',
  async (_, { dispatch }) => {
    try {
      const response = await subscriptionService.renewSubscription();

      if (response.status === 'success') {
        try {
          const session = await authService.refreshToken();
          dispatch(loginUser.fulfilled(session, '', { username: '', password: '' }));
        } catch (refreshError) {
          console.error('Failed to refresh token after renewing subscription:', refreshError);
        }
      }

      return response;
    } catch (error) {
      const apiError = error as ApiError;
      dispatch(
        setError(
          apiError.response?.data?.error || apiError.message || 'Failed to renew subscription',
        ),
      );
      throw error;
    }
  },
);

export const redeemCouponAction = createAsyncThunk(
  'subscription/redeemCoupon',
  async (payload: RedeemCouponPayload, { dispatch }) => {
    try {
      const response = await subscriptionService.redeemCoupon(payload);

      if (response.status === 'success') {
        try {
          const session = await authService.refreshToken();
          dispatch(loginUser.fulfilled(session, '', { username: '', password: '' }));
        } catch (refreshError) {
          console.error('Failed to refresh token after coupon redemption:', refreshError);
        }
      }

      return response;
    } catch (error) {
      const apiError = error as ApiError;
      dispatch(
        setError(
          apiError.response?.data?.error || apiError.message || 'Failed to redeem coupon',
        ),
      );
      throw error;
    }
  },
);

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    resetSubscription: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(enableTrial.pending, (state) => {
        state.loading = true;
      })
      .addCase(enableTrial.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.subscription) {
          const {
            type,
            expiryDate,
            startedAt,
            isSubscriptionActive,
            clientLimit,
            clientCount,
            canAddClients,
            daysRemaining,
          } = action.payload.subscription;
          state.type = type;
          state.expiresAt = expiryDate || null;
          state.startedAt = startedAt ?? null; // Ensure null if undefined
          state.isSubscriptionActive = isSubscriptionActive;
          state.clientLimit = clientLimit;
          state.clientCount = clientCount;
          state.canAddClients = canAddClients;
          state.daysRemaining = daysRemaining;
        }
      })
      .addCase(enableTrial.rejected, (state) => {
        state.loading = false;
      })
      .addCase(unsubscribeAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(unsubscribeAction.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.subscription) {
          state.type = action.payload.subscription.type || null;
          state.isSubscriptionActive = action.payload.subscription.isSubscriptionActive;
          state.isCancelled = action.payload.subscription.isCancelled || false;
          state.subscriptionEnd = action.payload.subscription.subscriptionEnd || null;
          state.remainingDays = action.payload.subscription.remainingDays;
          state.clientLimit = action.payload.subscription.clientLimit;
        }
      })
      .addCase(unsubscribeAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(renewSubscriptionAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(renewSubscriptionAction.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.subscription) {
          state.type = action.payload.subscription.type || null;
          state.isSubscriptionActive = action.payload.subscription.isSubscriptionActive;
          state.isCancelled = action.payload.subscription.isCancelled || false;
          state.subscriptionEnd = action.payload.subscription.subscriptionEnd || null;
          state.remainingDays = action.payload.subscription.remainingDays;
          state.clientLimit = action.payload.subscription.clientLimit;
        }
      })
      .addCase(renewSubscriptionAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(redeemCouponAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(redeemCouponAction.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.subscription) {
          state.type = action.payload.subscription.type || null;
          state.subscriptionSource = action.payload.subscription.subscriptionSource || 'COUPON';
          state.isSubscriptionActive = true;
          state.clientLimit = action.payload.subscription.clientLimit;
          state.expiresAt = action.payload.subscription.expiresAt || null;
        }
      })
      .addCase(redeemCouponAction.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { resetSubscription } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
